import posthog from "posthog-js";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { useEffect } from "react";

import { useUser } from "@/contexts/user-provider";

import { clinicClock, pickedDay } from "@/lib/clinic-clock";
import { getTimezone } from "@/lib/timezone";
import { zonedTodayMarker } from "@/lib/timezone-dates";

import {
  CalendarBookingSource,
  CalendarViewMode,
} from "@/types/enums/calendar";
import { BlockType, EventStatus, EventType } from "@/types/enums/event";

import { useTimezoneStore } from "@/stores/timezone-store";

interface OverflowInfo {
  count: number;
}

export interface BottomOverflowInfo extends OverflowInfo {
  /** Wall-clock minutes from the column's midnight, on the event's clock. */
  latestEndMinute?: number;
}

export interface TopOverflowInfo extends OverflowInfo {
  /** Wall-clock minutes from the column's midnight, on the event's clock. */
  earliestStartMinute?: number;
}

interface CalendarStore {
  viewMode: CalendarViewMode;
  displayedDate: Date;
  lastSetDate: Date | null;
  isCalendarSidebarCollapsed: boolean;
  filterProvidersByShiftOrAppointment: boolean;
  showShiftBackgrounds: boolean;
  /** Draw each location's appointments on that location's own clock. */
  showLocationTimes: boolean;
  showBookablesInDayView: boolean;
  selectedEventTypes: EventType[];
  /** Block types hidden from the calendar while Block events are shown. */
  hiddenBlockTypes: BlockType[];
  selectedEventStatuses: EventStatus[];
  selectedBookingSource: CalendarBookingSource;
  _savedEventTypesForSchedule: EventType[] | null;
  dragRescheduleEnabled: boolean;
  scheduleDaysShown: number;
  scheduleDaysBefore: number;

  startHour: number;
  endHour: number;
  isInitialized: boolean;
  topOverflow: TopOverflowInfo;
  bottomOverflow: BottomOverflowInfo;
  topPadding: number;
  bottomPadding: number;
  topOpened: boolean;
  bottomOpened: boolean;

  setViewMode: (mode: CalendarViewMode) => void;
  setDisplayedDate: (date: Date) => void;
  setIsCalendarSidebarCollapsed: (collapsed: boolean) => void;
  setFilterProvidersByShiftOrAppointment: (enabled: boolean) => void;
  setShowShiftBackgrounds: (enabled: boolean) => void;
  setShowLocationTimes: (enabled: boolean) => void;
  setShowBookablesInDayView: (enabled: boolean) => void;
  setSelectedEventTypes: (
    types: EventType[] | ((prev: EventType[]) => EventType[]),
  ) => void;
  setHiddenBlockTypes: (types: BlockType[]) => void;
  setSelectedEventStatuses: (
    statuses: EventStatus[] | ((prev: EventStatus[]) => EventStatus[]),
  ) => void;
  setSelectedBookingSource: (source: CalendarBookingSource) => void;
  setDragRescheduleEnabled: (enabled: boolean) => void;
  navigateDate: (
    direction: "prev" | "next",
    showWeekendsOnCalendar?: boolean,
  ) => void;
  loadMoreScheduleDays: () => void;
  loadEarlierScheduleDays: () => void;

  setTopOpened: (topOpened: boolean) => void;
  setBottomOpened: (bottomOpened: boolean) => void;
  setHours: (startHour: number, endHour: number) => void;
  initializeFromUser: (startHour: number, endHour: number) => void;
  updateFromUser: (startHour: number, endHour: number) => void;
  updateOverflowInfo: (
    topOverflow: TopOverflowInfo,
    bottomOverflow: BottomOverflowInfo,
  ) => void;
  setPadding: (topPadding: number, bottomPadding: number) => void;
  reset: () => void;
}

export const CALENDAR_STORE_KEY = "calendar-store";

const DEFAULT_EVENT_TYPES: EventType[] = [
  EventType.APPOINTMENT,
  EventType.BLOCK,
  EventType.GOOGLE_CALENDAR,
];

const DEFAULT_EVENT_STATUSES_LIST: EventStatus[] = Object.values(
  EventStatus,
).filter(
  (status): status is EventStatus =>
    typeof status === "string" && status !== EventStatus.CANCELLED,
);

const DEFAULT_START_HOUR = 0;
const DEFAULT_END_HOUR = 24;

const SCHEDULE_LOAD_MORE_DAYS = 7;

const DEFAULT_TOP_OVERFLOW = {
  count: 0,
  earliestStartMinute: undefined,
};

const DEFAULT_BOTTOM_OVERFLOW = {
  count: 0,
  latestEndMinute: undefined,
};

const isToday = (date: Date): boolean => {
  // Compare calendar-day markers in the clinic zone, not the browser's, so
  // "today" tracks the clinic day near midnight when the zones differ.
  return pickedDay(date) === clinicClock().today();
};

const getTodayAtMidnight = (): Date => clinicClock().todayMarker();

const getMillisecondsUntilMidnight = (): number => {
  // Schedule the "today" reset for the next CLINIC midnight, not the browser's,
  // so the displayed day rolls over in sync with getTodayAtMidnight()'s zone.
  const clock = clinicClock();
  return clock.dayRange(clock.today()).end.getTime() - Date.now();
};

// Store reference to timeout
let midnightTimeout: NodeJS.Timeout | null = null;

export const useCalendarStore = create<CalendarStore>()(
  persist(
    (set, get) => ({
      viewMode: CalendarViewMode.DAY,
      displayedDate: getTodayAtMidnight(),
      lastSetDate: null,
      isCalendarSidebarCollapsed: false,
      filterProvidersByShiftOrAppointment: false,
      showShiftBackgrounds: true,
      showLocationTimes: true,
      showBookablesInDayView: false,
      selectedEventTypes: DEFAULT_EVENT_TYPES,
      hiddenBlockTypes: [],
      selectedEventStatuses: DEFAULT_EVENT_STATUSES_LIST,
      selectedBookingSource: CalendarBookingSource.ALL,
      _savedEventTypesForSchedule: null,
      dragRescheduleEnabled: true,
      scheduleDaysShown: 1,
      scheduleDaysBefore: 0,

      startHour: DEFAULT_START_HOUR,
      endHour: DEFAULT_END_HOUR,
      isInitialized: false,
      topOverflow: DEFAULT_TOP_OVERFLOW,
      bottomOverflow: DEFAULT_BOTTOM_OVERFLOW,
      topPadding: 0,
      bottomPadding: 0,
      topOpened: false,
      bottomOpened: false,

      setViewMode: (mode) =>
        set({ viewMode: mode, scheduleDaysShown: 1, scheduleDaysBefore: 0 }),
      setDisplayedDate: (date) =>
        set({
          displayedDate: date,
          lastSetDate: getTodayAtMidnight(),
          scheduleDaysShown: 1,
          scheduleDaysBefore: 0,
        }),
      setIsCalendarSidebarCollapsed: (collapsed) =>
        set({ isCalendarSidebarCollapsed: collapsed }),
      setFilterProvidersByShiftOrAppointment: (enabled) => {
        posthog.capture(
          "calendar_filter_providers_by_shift_or_appointment_changed",
          { enabled: enabled ?? false },
        );
        set({ filterProvidersByShiftOrAppointment: enabled });
      },
      setShowShiftBackgrounds: (enabled) => {
        posthog.capture("calendar_show_shift_backgrounds_changed", {
          enabled: enabled ?? false,
        });
        set({ showShiftBackgrounds: enabled });
      },
      setShowLocationTimes: (enabled) => {
        posthog.capture("calendar_show_location_times_changed", {
          enabled: enabled ?? false,
        });
        set({ showLocationTimes: enabled });
      },
      setShowBookablesInDayView: (enabled) => {
        posthog.capture("calendar_show_bookables_in_day_view_changed", {
          enabled: enabled ?? false,
        });
        set({ showBookablesInDayView: enabled });
      },
      setSelectedEventTypes: (typesOrUpdater) => {
        const newTypes =
          typeof typesOrUpdater === "function"
            ? typesOrUpdater(get().selectedEventTypes)
            : typesOrUpdater;
        set({ selectedEventTypes: newTypes });
      },
      setHiddenBlockTypes: (types) => set({ hiddenBlockTypes: types }),
      setSelectedEventStatuses: (statusesOrUpdater) => {
        const newStatuses =
          typeof statusesOrUpdater === "function"
            ? statusesOrUpdater(get().selectedEventStatuses)
            : statusesOrUpdater;
        set({ selectedEventStatuses: newStatuses });
      },
      setSelectedBookingSource: (source) => {
        posthog.capture("calendar_booking_source_filter_changed", { source });
        set({ selectedBookingSource: source });
      },
      setDragRescheduleEnabled: (enabled) => {
        posthog.capture("calendar_drag_reschedule_enabled_changed", {
          enabled: enabled ?? false,
        });
        set({ dragRescheduleEnabled: enabled });
      },
      navigateDate: (direction, showWeekendsOnCalendar = true) => {
        const { displayedDate, viewMode } = get();
        const newDate = new Date(displayedDate);

        if (viewMode === CalendarViewMode.WEEK) {
          newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
        } else {
          newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
          if (!showWeekendsOnCalendar) {
            if (direction === "next") {
              if (newDate.getDay() === 6) {
                newDate.setDate(newDate.getDate() + 2);
              } else if (newDate.getDay() === 0) {
                newDate.setDate(newDate.getDate() + 1);
              }
            } else {
              if (newDate.getDay() === 0) {
                newDate.setDate(newDate.getDate() - 2);
              } else if (newDate.getDay() === 6) {
                newDate.setDate(newDate.getDate() - 1);
              }
            }
          }
        }
        set({
          displayedDate: newDate,
          scheduleDaysShown: 1,
          scheduleDaysBefore: 0,
        });
      },

      loadMoreScheduleDays: () =>
        set((state) => ({
          scheduleDaysShown: state.scheduleDaysShown + SCHEDULE_LOAD_MORE_DAYS,
        })),

      loadEarlierScheduleDays: () =>
        set((state) => ({
          scheduleDaysBefore:
            state.scheduleDaysBefore + SCHEDULE_LOAD_MORE_DAYS,
        })),

      setTopOpened: (topOpened: boolean) => {
        set({ topOpened });
      },

      setBottomOpened: (bottomOpened: boolean) => {
        set({ bottomOpened });
      },

      setHours: (startHour: number, endHour: number) => {
        set({ startHour, endHour });
      },

      initializeFromUser: (startHour: number, endHour: number) => {
        set({
          startHour,
          endHour,
          isInitialized: true,
          topOverflow: DEFAULT_TOP_OVERFLOW,
          bottomOverflow: DEFAULT_BOTTOM_OVERFLOW,
        });
      },

      updateFromUser: (startHour: number, endHour: number) => {
        set({
          startHour,
          endHour,
        });
      },

      updateOverflowInfo: (
        topOverflow: { count: number; earliestStart?: Date },
        bottomOverflow: { count: number; latestEnd?: Date },
      ) => {
        set({ topOverflow, bottomOverflow });
      },

      setPadding: (topPadding: number, bottomPadding: number) => {
        set({ topPadding, bottomPadding });
      },

      reset: () => {
        set({
          startHour: DEFAULT_START_HOUR,
          endHour: DEFAULT_END_HOUR,
          isInitialized: false,
          topOverflow: DEFAULT_TOP_OVERFLOW,
          bottomOverflow: DEFAULT_BOTTOM_OVERFLOW,
        });
      },
    }),
    {
      name: CALENDAR_STORE_KEY,
      partialize: (state) => ({
        viewMode: state.viewMode,
        displayedDate: state.displayedDate,
        lastSetDate: state.lastSetDate,
        isCalendarSidebarCollapsed: state.isCalendarSidebarCollapsed,
        filterProvidersByShiftOrAppointment:
          state.filterProvidersByShiftOrAppointment,
        showShiftBackgrounds: state.showShiftBackgrounds,
        showLocationTimes: state.showLocationTimes,
        showBookablesInDayView: state.showBookablesInDayView,
        selectedEventTypes: state.selectedEventTypes,
        hiddenBlockTypes: state.hiddenBlockTypes,
        selectedEventStatuses: state.selectedEventStatuses,
        selectedBookingSource: state.selectedBookingSource,
        _savedEventTypesForSchedule: state._savedEventTypesForSchedule,
        dragRescheduleEnabled: state.dragRescheduleEnabled,
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;

          try {
            const data = JSON.parse(str);
            if (data.state?.displayedDate) {
              data.state.displayedDate = new Date(data.state.displayedDate);
            }
            // Handle lastSetDate - if it doesn't exist (old version), initialize it to today
            const today = getTodayAtMidnight();
            if (data.state?.lastSetDate) {
              data.state.lastSetDate = new Date(data.state.lastSetDate);

              // Check if lastSetDate is before today
              const lastSet = new Date(data.state.lastSetDate);
              lastSet.setHours(0, 0, 0, 0);

              if (lastSet < today) {
                // Reset displayedDate to today if lastSetDate is before today
                data.state.displayedDate = today;
                data.state.lastSetDate = today;
              }
            } else {
              // If lastSetDate doesn't exist (old version), set it to today
              data.state.lastSetDate = today;
            }
            // Recover from stale _savedEventTypesForSchedule (e.g., browser closed
            // while on schedule view). Restore the user's real selection and clear the field.
            if (data.state?._savedEventTypesForSchedule) {
              data.state.selectedEventTypes =
                data.state._savedEventTypesForSchedule;
              data.state._savedEventTypesForSchedule = null;
            }

            return data;
          } catch (error) {
            console.warn("Failed to parse calendar store data:", error);
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            const serializedValue = {
              ...value,
              state: {
                ...value.state,
              },
            };
            localStorage.setItem(name, JSON.stringify(serializedValue));
          } catch (error) {
            console.warn("Failed to save calendar store data:", error);
          }
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
      version: 6,
      migrate: (persisted: unknown, version: number) => {
        const state = (persisted as Record<string, unknown>) ?? {};
        if (version < 3) {
          // v3: default CANCELLED to excluded for existing users
          const statuses = state.selectedEventStatuses as
            | EventStatus[]
            | undefined;
          if (statuses) {
            state.selectedEventStatuses = statuses.filter(
              (s) => s !== EventStatus.CANCELLED,
            );
          }
        }
        if (version < 4) {
          // v4: GOOGLE_CALENDAR was added to DEFAULT_EVENT_TYPES. Add it to
          // existing users' selectedEventTypes so Google events show up by
          // default after the upgrade; the default array only applies to
          // first-time users.
          const types = state.selectedEventTypes as EventType[] | undefined;
          if (types && !types.includes(EventType.GOOGLE_CALENDAR)) {
            state.selectedEventTypes = [...types, EventType.GOOGLE_CALENDAR];
          }
        }
        if (version < 5) {
          // v5: drag-to-reschedule pref introduced. Default to disabled
          // (opt-in) for existing users.
          if (state.dragRescheduleEnabled === undefined) {
            state.dragRescheduleEnabled = false;
          }
        }
        if (version < 6) {
          // v6: drag-to-reschedule graduates to on-by-default. Flip every
          // existing persisted user on once — including the cohort that
          // reached v5 before the v5 step shipped, for whom it never ran.
          state.dragRescheduleEnabled = true;
        }
        return state as unknown as CalendarStore;
      },
    },
  ),
);

const setupMidnightReset = () => {
  if (midnightTimeout) {
    clearTimeout(midnightTimeout);
  }

  const msUntilMidnight = getMillisecondsUntilMidnight();

  midnightTimeout = setTimeout(() => {
    const store = useCalendarStore.getState();
    const currentDisplayedDate = store.displayedDate;

    // Only reset if the current displayed date is not today
    // (in case user manually navigated to a future/past date)
    if (!isToday(currentDisplayedDate)) {
      store.setDisplayedDate(getTodayAtMidnight());
    }
    setupMidnightReset();
  }, msUntilMidnight);
};

if (typeof window !== "undefined") {
  setupMidnightReset();

  // The clinic's zone resolves after this module loads, and can change when
  // staff switch location. Follow it: reschedule the midnight reset, and if
  // the calendar was showing today, show today in the new zone. A date
  // someone navigated to stays put.
  let followedTimezone = getTimezone();
  useTimezoneStore.subscribe(() => {
    const timezone = getTimezone();
    if (timezone === followedTimezone) return;
    const previousToday = zonedTodayMarker(followedTimezone);
    followedTimezone = timezone;
    const { displayedDate } = useCalendarStore.getState();
    if (displayedDate.toDateString() === previousToday.toDateString()) {
      useCalendarStore.setState({ displayedDate: zonedTodayMarker(timezone) });
    }
    setupMidnightReset();
  });
}

// Hook to ensure proper initialization and updates with user preferences
export const useCalendarHours = () => {
  const { currentUser } = useUser();
  const {
    startHour,
    endHour,
    isInitialized,
    topOverflow,
    bottomOverflow,
    initializeFromUser,
    updateFromUser,
    setHours,
    setTopOpened,
    setBottomOpened,
    topOpened,
    bottomOpened,
  } = useCalendarStore();

  // Initialize and update from user preferences when user data changes
  useEffect(() => {
    if (topOpened || bottomOpened) return;
    if (currentUser) {
      const userStartHour = currentUser.startCalendarHour ?? DEFAULT_START_HOUR;
      const userEndHour = currentUser.endCalendarHour ?? DEFAULT_END_HOUR;

      if (!isInitialized) {
        // Initial setup
        initializeFromUser(userStartHour, userEndHour);
      } else {
        // Update existing values when preferences change
        updateFromUser(userStartHour, userEndHour);
      }
    }
  }, [
    currentUser?.startCalendarHour,
    currentUser?.endCalendarHour,
    isInitialized,
    initializeFromUser,
    updateFromUser,
    topOpened,
    bottomOpened,
  ]);

  // Overflow payloads are the grid's own rows, each event read on its clock.
  // An event from the day before reads as midnight, one into the next as the
  // last hour.
  const gridHour = (minute: number): number =>
    Math.min(23, Math.max(0, Math.floor(minute / 60)));

  const toggleTop = () => {
    if (topOpened) {
      setTopOpened(false);
      return;
    }

    setTopOpened(true);
    setHours(
      Math.max(
        0,
        (topOverflow.earliestStartMinute !== undefined
          ? gridHour(topOverflow.earliestStartMinute)
          : DEFAULT_START_HOUR + 1) - 1,
      ),
      endHour,
    );
  };

  const toggleBottom = () => {
    if (bottomOpened) {
      setBottomOpened(false);
      return;
    }

    setBottomOpened(true);
    const latestEndHour = Math.min(
      23,
      (bottomOverflow.latestEndMinute !== undefined
        ? gridHour(bottomOverflow.latestEndMinute)
        : DEFAULT_END_HOUR - 1) + 1,
    );
    setHours(startHour, latestEndHour);
  };

  return {
    startHour,
    endHour,
    isInitialized,
    topOverflow,
    bottomOverflow,
    topOpened,
    bottomOpened,
    toggleTop,
    toggleBottom,
    setHours: useCalendarStore.getState().setHours,
    updateOverflowInfo: useCalendarStore.getState().updateOverflowInfo,
    reset: useCalendarStore.getState().reset,
  };
};
